import React, { useContext, useEffect, useState } from 'react';

import { toJS } from 'mobx';
import { MobXProviderContext, observer } from 'mobx-react';

import { Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import { kebabCase } from 'lodash';

import '../../css/GoogleClassroomRosterConnectionModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import dialogManager, { DIALOG_NAMES } from '../../managers/DialogManager';

import GoogleClassroomService from '../../services/GoogleClassroomService';
import PopupService from '../../services/PopupService';
import UserService from '../../services/UserService';
import UtilityService from '../../services/UtilityService';

import { getStyleVar } from '../../utils';

const GoogleClassroomRosterConnectionModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'cancel',
    buttonKeyConfirmLabel = 'link', // 'link' | 'unlink' | 'import'
    className = 'GoogleClassroomRosterConnectionModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    currentClassroom,
    currentUser,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    initHeight,
    initWidth,
    minWidth = 540,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    t,
    top = window.innerHeight / 6
  } = props;

  const {
    googleClassroomManager,
  } = useContext(MobXProviderContext);

  const [isLoadingGoogleClassroomRosterConnectionModal, setIsLoadingGoogleClassroomRosterConnectionModal] = useState(true);

  const GenericModal = SatCoreComponent('GenericModal');
  const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');

  const isLinkedGoogleClassroomRosterUser = !!currentUser?.googleClassroomId;

  const isMultiSelectionMode = !currentUser;

  const userFullNameStr = currentUser ? UserService.getUserFullNameStr(currentUser) : undefined;

  let headerMsg;
  if (isMultiSelectionMode) {
    headerMsg = t('headerMsgMultiUsers');
  } else if (isLinkedGoogleClassroomRosterUser) {
    headerMsg = t('headerMsgLinkedUser');
  } else {
    headerMsg = t('headerMsgSingleUser');
  }

  useEffect(() => {
    (async () => {
      await GoogleClassroomService.fetchGoogleClassroomRosterMap({
        classroom: currentClassroom
      });
      setIsLoadingGoogleClassroomRosterConnectionModal(false);
    })();
  }, []);

  const handleChangeGoogleClassroomRosterOption = (event, googleClassroomRosterOption = {}) => {
    const rosterId = googleClassroomRosterOption?.id;
    if (!rosterId) {
      return;
    }
    const checked = !!event?.target?.checked;

    switch (rosterId) {
      case 'selectAll':
        if (checked) {
          for (const googleClassroomRoster of toJS(googleClassroomManager.unlinkedGoogleClassroomRosterUsersArray)) {
            googleClassroomManager.addToSelectedGoogleClassroomRosterOptionsMap(googleClassroomRoster);
          }
        } else {
          googleClassroomManager.clearSelectedGoogleClassroomRosterOptionsMap();
        }
        break;
      default:
        if (checked) {
          if (!isMultiSelectionMode && googleClassroomManager.selectedGoogleClassroomRosterOptionsMap.size) {
            googleClassroomManager.clearSelectedGoogleClassroomRosterOptionsMap();
          }
          googleClassroomManager.addToSelectedGoogleClassroomRosterOptionsMap(googleClassroomRosterOption);
        } else {
          googleClassroomManager.removeFromSelectedGoogleClassroomRosterOptionsMap(googleClassroomRosterOption?.id);
        }
        break;
    }
  };

  const handleCloseGoogleClassroomRosterConnectionModal = async (_event, actionButtonData = {}) => {
    setIsLoadingGoogleClassroomRosterConnectionModal(true);

    const confirmed = !!(actionButtonData?.className?.includes?.(kebabCase(buttonKeyConfirmLabel)));

    const cleanup = async ({ success } = {}) => {
      if (success) {
        googleClassroomManager.clearGoogleClassroomRosterMap();
        googleClassroomManager.clearSelectedGoogleClassroomRosterOptionsMap();
        props.onClose?.();
        setIsLoadingGoogleClassroomRosterConnectionModal(false);
        if (confirmed && typeof props.fetchRosterData === 'function') {
          await props.fetchRosterData();
        }
      } else {
        setIsLoadingGoogleClassroomRosterConnectionModal(false);
      }
    };

    if (confirmed) {
      // user confirmed changes via action button
      if (isMultiSelectionMode) {
        const success = await handleImportSelectedGoogleClassroomRosterUsers();
        await cleanup({ success });
      } else if (isLinkedGoogleClassroomRosterUser) {
        const success = await handleDisconnectSelectedGoogleClassroomRosterUser();
        await cleanup({ success });
      } else {
        const success = await handleConnectSelectedGoogleClassroomRosterUser();
        await cleanup({ success });
      }
    } else {
      // user did not confirm changes
      await cleanup({ success: true });
    }
  };

  const handleImportSelectedGoogleClassroomRosterUsers = async () => {
    const response = await GoogleClassroomService.importGoogleClassroomRosterUsers({
      classroom: currentClassroom
    });
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const handleConnectSelectedGoogleClassroomRosterUser = async () => {
    const currentGoogleClassroomUser = googleClassroomManager.selectedGoogleClassroomsRosterUsersArray?.[0];

    const response = await GoogleClassroomService.connectGoogleToUser({
      googleClassroomUser: currentGoogleClassroomUser,
      user: currentUser
    });
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const handleDisconnectSelectedGoogleClassroomRosterUser = async () => {
    const response = await GoogleClassroomService.disconnectGoogleFromUser({
      user: currentUser
    });
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const renderGoogleClassroomRosterConnectionModalBody = () => {
    if (isLoadingGoogleClassroomRosterConnectionModal) {
      return <Loader active />;
    }

    const hasUnlinkedGoogleClassroomRosterUsers = !!googleClassroomManager.unlinkedGoogleClassroomRosterUsersArray?.length;

    let googleClassroomRosterConnectionModalBodyHeaderLabel;
    if (!hasUnlinkedGoogleClassroomRosterUsers && !isLinkedGoogleClassroomRosterUser) {
      googleClassroomRosterConnectionModalBodyHeaderLabel = isMultiSelectionMode ? (
        t('zeroStateMultiGoogleClassroomRosterConnectionModalBodyHeaderLabel')
      ) : (
        t('zeroStateSingleGoogleClassroomRosterConnectionModalBodyHeaderLabel')
      );
    } else if (isMultiSelectionMode) {
      googleClassroomRosterConnectionModalBodyHeaderLabel = t('multiGoogleClassroomRosterConnectionModalBodyHeaderLabel');
    } else if (isLinkedGoogleClassroomRosterUser) {
      googleClassroomRosterConnectionModalBodyHeaderLabel = t('linkedGoogleClassroomRosterConnectionModalBodyHeaderLabel', {
        currentUserName: userFullNameStr,
      });
    } else {
      googleClassroomRosterConnectionModalBodyHeaderLabel = t('singleGoogleClassroomRosterConnectionModalBodyHeaderLabel', {
        currentUserName: userFullNameStr,
      });
    }

    return (
      <>
        <div className='google-classroom-roster-connection-modal-body-top'>
          <div className='google-classroom-roster-connection-modal-body-top-left'>
            <div className='google-classroom-roster-connection-modal-body-header-label'>
              {t(googleClassroomRosterConnectionModalBodyHeaderLabel)}
            </div>
          </div>
          <div className='google-classroom-roster-connection-modal-body-top-mid'>
            {/* placeholder */}
          </div>
          <div className='google-classroom-roster-connection-modal-body-top-right'>
            {/* TODO remove */}
            {/* <div className='google-classroom-roster-connection-reset-all'
              onClick={() => {
                googleClassroomManager.clearSelectedGoogleClassroomRosterOptionsMap();
              }}>
              {t('resetAll')}
            </div> */}
          </div>
        </div>
        <div className='google-classroom-roster-connection-modal-options'>
          {isMultiSelectionMode && hasUnlinkedGoogleClassroomRosterUsers && (
            renderSelectAllGoogleClassroomRosterUsersOption()
          )}
          {renderGoogleClassroomRosterOptions()}
        </div>
      </>
    );
  };

  const renderSelectAllGoogleClassroomRosterUsersOption = () => {
    const {
      selectedGoogleClassroomRosterOptionsMap,
      unlinkedGoogleClassroomRosterUsersArray
    } = googleClassroomManager;
    const option = {
      id: 'selectAll'
    };
    const isAllSelectedRosters = unlinkedGoogleClassroomRosterUsersArray.length && (
      unlinkedGoogleClassroomRosterUsersArray.length === selectedGoogleClassroomRosterOptionsMap.size
    );

    const checked = isAllSelectedRosters;
    const disabled = false;

    return (
      <div className={classNames('google-classroom-roster-connection-modal-option-row', {
        'google-classroom-roster-connection-modal-option-row-selectAll': true
      })}>
        <div className={classNames('google-classroom-roster-option-checkbox-wrapper')}>
          <IndeterminateCheckbox
            aria-label={t('selectAll')}
            checked={checked}
            disabled={disabled}
            onChange={(event) => handleChangeGoogleClassroomRosterOption(event, option)} />
          <div className={classNames('checkbox-label google-classroom-roster-option-checkbox-label', {
            checked: isAllSelectedRosters,
            disabled
          })} onClick={() => {
            if (!disabled) {
              const pseudoEvent = {
                target: { checked: !checked }
              };
              handleChangeGoogleClassroomRosterOption(pseudoEvent, option);
            }
          }}>
            {t('selectAll')}
          </div>
        </div>
      </div>
    );
  };

  const renderGoogleClassroomRosterOptions = () => {
    if (isLinkedGoogleClassroomRosterUser) {
      const googleClassroomUserId = currentUser.googleClassroomId;
      const currentGoogleClassroomRosterUser = googleClassroomManager.googleClassroomRosterMap.get(googleClassroomUserId) || {
        // TODO DEMO-2855 - google classroom user data was not found, likely due to the student being linked via another classroom roster.
        // TODO DEMO-2855 - this will be discussed in the upcoming review on 6/5/25.
        firstName: currentUser.googleClassroomId
      };
      // render the linked current roster user as a readonly (disabled) option
      // (to show the user it is the current 'linked' google classroom roster)
      return [currentGoogleClassroomRosterUser].map((googleClassroomRoster) => {
        return renderGoogleClassroomRosterOption(googleClassroomRoster, { disabled: true, forceSelected: true });
      });
    } else {
      return googleClassroomManager.unlinkedGoogleClassroomRosterUsersArray?.map((googleClassroomRoster) => {
        return renderGoogleClassroomRosterOption(googleClassroomRoster);
      });
    }
  };

  const renderGoogleClassroomRosterOption = (googleClassroomRoster, {
    forceSelected,
    disabled
  } = {}) => {
    const {
      selectedGoogleClassroomRosterOptionsMap
    } = googleClassroomManager;
    if (!googleClassroomRoster?.id) {
      return null;
    }
    const googleClassroomRosterNameObj = UtilityService.reactHtmlParserWrapper(googleClassroomRoster.name);

    const checked = forceSelected || selectedGoogleClassroomRosterOptionsMap.has(googleClassroomRoster.id);

    return googleClassroomRosterNameObj ? (
      <div key={googleClassroomRoster.id}
        className={classNames('google-classroom-roster-connection-modal-option-row')}>
        <div className={classNames('google-classroom-roster-option-checkbox-wrapper')}>
          <IndeterminateCheckbox
            aria-label={googleClassroomRosterNameObj.stripped}
            checked={checked}
            disabled={disabled}
            onChange={(event) => handleChangeGoogleClassroomRosterOption(event, googleClassroomRoster)}
            radio={!isMultiSelectionMode} />
          {PopupService.renderPopup({
            className: 'google-classroom-roster-option-checkbox-label-popup',
            content: googleClassroomRosterNameObj.parsed,
            mouseEnterDelay: 1000,
            position: 'top left',
            trigger: (
              <div className={classNames('checkbox-label google-classroom-roster-option-checkbox-label', {
                checked,
                disabled
              })} onClick={() => {
                if (!disabled) {
                  const pseudoEvent = {
                    target: { checked: !checked }
                  };
                  handleChangeGoogleClassroomRosterOption(pseudoEvent, googleClassroomRoster);
                }
              }}>
                {googleClassroomRosterNameObj.stripped}
              </div>
            ),
            wide: false
          })}
        </div>
      </div>
    ) : null;
  };

  const renderErrorModal = ({
    success,
    ...props
  } = {}) => {
    renderSuccessOrFailureModal({ ...props, success });
  };

  const renderSuccessOrFailureModal = ({
    cancelButtonClassName = '',
    cancelButtonName = '',
    className = '',
    confirmButtonClassName = '',
    confirmButtonName = '',
    confirmHandler = () => {},
    message = '',
    success = false,
    title = '',
  } = {}) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.CONFIRM, {
      cancelButtonClass: cancelButtonClassName,
      cancelButtonName: cancelButtonName || t('close'),
      className: className || classNames('google-classroom-roster-connection-confirm-modal', {
        error: !success,
        success
      }),
      confirmButtonClass: confirmButtonClassName,
      confirmButtonName,
      confirmHandler,
      message: message || t('defaultErrorMessage'),
      title: title || t('defaultErrorHeaderLabel')
    },
    () => dialogManager.closeDialog(DIALOG_NAMES.CONFIRM));
  };

  const disableConfirmButton = !googleClassroomManager.selectedGoogleClassroomRosterOptionsMap.size && !isLinkedGoogleClassroomRosterUser;

  let minHeight, isZeroState;
  if (props.minHeight) {
    minHeight = props.minHeight;
  } else if (isLinkedGoogleClassroomRosterUser) {
    minHeight = getStyleVar('--google-classroom-roster-connection-modal-linked-min-height');
  } else if (!isLoadingGoogleClassroomRosterConnectionModal && !googleClassroomManager.unlinkedGoogleClassroomRosterUsersArray?.length) {
    isZeroState = true;
    minHeight = getStyleVar('--google-classroom-roster-connection-modal-zerostate-min-height');
  } else {
    minHeight = getStyleVar('--google-classroom-roster-connection-modal-min-height');
  }

  return (
    <GenericModal
      bodyMsg={renderGoogleClassroomRosterConnectionModalBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={!isZeroState ? buttonKeyConfirmLabel : ''}
      className={classNames(className, {
        'zero-state': isZeroState
      })}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableConfirmButton={disableConfirmButton}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={headerMsg}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isConfirmButtonLoading={false}
      isLoading={isLoadingGoogleClassroomRosterConnectionModal}
      isOpen={isOpen}
      onClose={handleCloseGoogleClassroomRosterConnectionModal}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top} />
  );
});

export default GoogleClassroomRosterConnectionModal;

SatCoreRegister('GoogleClassroomRosterConnectionModal', GoogleClassroomRosterConnectionModal);
